import React, { useMemo, useState } from 'react';
import { Card, Nav } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import {
  CalibrationSessionState,
  useCalibrationSessionsQuery,
} from '../../../__generated__/graphql';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { CalibrationSessionCard } from './CalibrationSessionCard';
import ServerError from '../../../../../components/ServerError';

type Tab = 'inProgress' | 'all';

const i18Path = 'performanceReview.calibration';

export const CalibrationSessionList: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const t = useNamespacedTranslation(i18Path);
  const [activeTab, setActiveTab] = useState<Tab>('inProgress');
  const { data, loading, error } = useCalibrationSessionsQuery();

  const sessions = useMemo(() => {
    const allSessions = data?.calibrationSessions ?? [];
    if (activeTab === 'inProgress') {
      return allSessions.filter(
        (s) => s.flair__State__c === CalibrationSessionState.InProgress,
      );
    }
    return allSessions;
  }, [data, activeTab]);

  if (error) {
    return <ServerError />;
  }

  return (
    <>
      <Nav
        variant="tabs"
        className="mb-3"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab((key ?? 'inProgress') as Tab)}>
        <Nav.Item>
          <Nav.Link eventKey="inProgress">{t('tabs.inProgress')}</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="all">{t('tabs.all')}</Nav.Link>
        </Nav.Item>
      </Nav>

      {loading && !data ? (
        <>
          {[0, 1, 2].map((i) => (
            <Card className="mb-3" key={i}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Skeleton width={200} height={20} className="mb-1 me-2" />
                  </div>
                  <Skeleton width={80} height={24} />
                </div>
              </Card.Body>
            </Card>
          ))}
        </>
      ) : sessions.length === 0 ? (
        <p className="text-muted text-center py-4">
          {t('sessionList.noSessions')}
        </p>
      ) : (
        sessions.map((session) => (
          <CalibrationSessionCard key={session.Id} session={session} />
        ))
      )}
    </>
  );
};
